import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BREAKPOINTS } from '@/lib/breakpoints';
import '@/common/styles/ui-primitives.css';
import './report/report.css';

interface PagePreviewModalProps {
  html: string;
  title?: string;
  onClose: () => void;
}

const MOBILE_FRAME_WIDTH = 390;

type PreviewMode = 'desktop' | 'mobile';

/** Fullscreen preview overlay for a built page. Toolbar at top with a
 *  desktop/mobile toggle so the user can sanity-check the responsive
 *  breakpoint behavior without leaving the chat panel. On real mobile
 *  devices (viewport ≤ 767px) the toggle is hidden and we force mobile
 *  mode — the device IS a phone, no need to fake one. */
export default function PagePreviewModal({ html, title, onClose }: PagePreviewModalProps) {
  const [isViewportMobile, setIsViewportMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.compactMax,
  );
  const [mode, setMode] = useState<PreviewMode>(
    isViewportMobile ? 'mobile' : 'desktop',
  );

  // Esc closes; lock body scroll while open. Track viewport changes so the
  // toggle hides/shows correctly if the user resizes across the breakpoint
  // (or rotates a tablet) without reopening the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onResize = () => {
      const nowMobile = window.innerWidth <= BREAKPOINTS.compactMax;
      setIsViewportMobile((prev) => (prev === nowMobile ? prev : nowMobile));
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // When the viewport crosses into mobile, force-mobile mode (the toggle
  // also disappears via showToggle below). When it crosses back to desktop
  // we leave the user's last choice alone — they may have intentionally
  // selected mobile preview while on a wide screen.
  useEffect(() => {
    if (isViewportMobile && mode !== 'mobile') setMode('mobile');
  }, [isViewportMobile, mode]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const showToggle = !isViewportMobile;
  const isMobileFrame = mode === 'mobile';

  return createPortal(
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10_000,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          background: 'rgba(20, 20, 20, 0.95)',
          color: '#fff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '40%',
          }}
        >
          {title || 'Preview'}
        </div>
        {showToggle && (
          <div className="uiViewToggle" role="tablist" aria-label="Preview viewport">
            <ToggleButton
              active={mode === 'desktop'}
              onClick={() => setMode('desktop')}
              label="Desktop"
              icon={<DesktopIcon />}
            />
            <ToggleButton
              active={mode === 'mobile'}
              onClick={() => setMode('mobile')}
              label="Mobile"
              icon={<MobileIcon />}
            />
          </div>
        )}
        <button
          type="button"
          className="report-share-link-btn"
          onClick={onClose}
          aria-label="Close preview"
          title="Close (Esc)"
        >
          Close
        </button>
      </div>

      <div
        className="xv-scroll"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          alignItems: isMobileFrame ? 'center' : 'stretch',
          justifyContent: 'center',
          padding: isMobileFrame ? '24px 16px' : 0,
          overflow: 'auto',
        }}
      >
        {isMobileFrame ? (
          <div
            style={{
              width: MOBILE_FRAME_WIDTH,
              maxWidth: '100%',
              height: '100%',
              maxHeight: 844,
              borderRadius: 32,
              border: '8px solid #1a1a1a',
              background: '#000',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              overflow: 'hidden',
            }}
          >
            <iframe
              title={`${title || 'Page'} preview (mobile)`}
              srcDoc={html}
              sandbox="allow-scripts allow-popups allow-forms"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'var(--color-surface)',
              }}
            />
          </div>
        ) : (
          <iframe
            title={`${title || 'Page'} preview`}
            srcDoc={html}
            sandbox="allow-scripts allow-popups allow-forms"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'var(--color-surface)',
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

function ToggleButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`uiViewToggle__btn${active ? ' is-active' : ''}`}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function DesktopIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="6" y="2" width="12" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12" y2="18" />
    </svg>
  );
}
